/* SR SCALE — the one mapping between a slider handle and a number.
   🚨 EVERY CONTINUOUS SLIDER ON THIS STORE USES THIS. There is no second copy.

   ── WHY IT EXISTS ────────────────────────────────────────────────────────────
   A slider's job is to let a shopper reach any value in the range with a
   comfortable amount of travel. A LINEAR slider does that only when the values are
   evenly spread, and jewelry prices never are. Measured 2026-08-10 on the diamond
   tab, where the browse panel ran a 100-step linear scale over $125–$198,275:

       one step                        $1,982
       stones inside the FIRST step    6,949 of 7,834  = 88.7%
       the median diamond ($505)       position 0.2 of 100
       positions 50–100                bought nothing under $99,000

   So half the track was dead and the half the shopper wanted was a single pixel.
   Owner, 2026-08-10: *"It appears as if there is such a wide range that's making the
   slider act funky… can we make it so the slider takes up more space for the
   majority of products and then as it gets to higher prices it has more incremental
   jumps."* That is a LOG scale, and it is what this is.

   Under log, travel is proportional to RATIO rather than to dollars — the distance
   from $500 to $1,000 equals the distance from $50,000 to $100,000, which is what a
   person means by "about twice as much". On that same diamond pool:

       median  $505      0.2%  ->  18.9%  of the track
       p90     $2,535    1.2%  ->  40.8%
       p99     $23,415  11.8%  ->  71.0%
       distinct values selectable at or under $2,000:  1  ->  ~250

   ── THE RULES, AND THEY ARE THE POINT OF THE FILE ────────────────────────────
   1. VALUES ARE EXACT AND NEVER SNAPPED TO ROUND NUMBERS. Owner, 2026-08-10:
      *"can we make it so the numbers are exact and not rounded?"* An earlier cut of
      this snapped to $25/$100/$1,000 bands so a handle would land on "$1,000"
      rather than "$999". That is a nicer number and a WRONG one: the grid filters on
      what the slider emits, so a shopper reading $1,000 while $1,014 is applied has
      been told a small lie by their own control. `toV` returns the value at that
      position and the caller formats it to the unit's own precision — whole dollars,
      0.01 ct, 0.01 mm.
   2. THE TWO ENDS ARE EXACT BY CONSTRUCTION. `toV(0) === min` and
      `toV(steps) === max`, returned directly rather than computed. Both directions
      of an inexact end are a real bug and each was hit: a ceiling rounded UP invents
      a price the catalogue does not reach and leaves a "Price" chip showing for a
      filter nobody set, and one rounded DOWN silently drops the dearest stones from
      the widest setting.
   3. NEVER RE-DERIVE A DISPLAYED VALUE FROM A HANDLE POSITION WHEN YOU ALREADY
      KNOW IT. A position is quantised and a price is not, so the round trip loses.
      This is what printed **$2,107 over a filter doing $1,000** (owner, 2026-08-10):
      both handles quantised into one step and the readout showed that step's edge.
      Show the real value until the shopper moves a handle; after that the derived
      value IS what will be applied, so it is the truth.
   4. LINEAR IS STILL RIGHT SOMETIMES. Below `LOG_RATIO` there is no skew worth
      correcting, and on a physical dimension (millimetres) even travel is what a
      shopper expects. `make()` chooses, so no caller has to.

   ── ADDING A SLIDER LATER ────────────────────────────────────────────────────
   Load this file first, call `SRScale.make(min, max)`, drive the input in POSITIONS
   (`0 … scale.steps`), and format `scale.toV(pos)` for display. Do not write your
   own mapping — `verify_slider_scales.py` fails the build on one.
*/
(function () {
  'use strict';

  var SRScale = {
    // How many positions a slider offers. 1,000 gives ~0.7% between neighbours on
    // the widest range here ($125–$198,275), which is finer than a shopper can aim
    // and fine enough that no whole dollar under ~$150 is unreachable.
    // ⚠️ A native `<input type=range>` must carry `max="<steps>"` and `step="1"` to
    // match; the position IS the input's value.
    STEPS: 1000,

    // Below this max/min ratio a linear scale is not meaningfully skewed and the
    // extra indirection buys nothing. 25x is roughly where the median of a
    // real-world price pool stops being squashed into the first tenth of the track.
    LOG_RATIO: 25,

    /* make(min, max, opts) -> { steps, isLog, toI(value), toV(position) }
       opts.force: 'log' | 'linear' to override the ratio test. */
    make: function (min, max, opts) {
      opts = opts || {};
      var steps = this.STEPS;
      // A log scale needs a positive floor and a real span. Anything degenerate —
      // a single-value pool, a zero or negative minimum — falls back to linear,
      // which is correct: there is no skew to correct and no range to travel.
      var span = max - min;
      // 🚨 A ZERO MINIMUM IS THE COMMON CASE, NOT AN EDGE CASE, AND REFUSING TO
      // CURVE ON IT SILENTLY DISABLED THIS EVERYWHERE IT WAS MOST NEEDED.
      // Shopify reports a collection's price filter as starting at 0 — that is the
      // FILTER's floor, not the cheapest sapphire — so all 21 species pages arrive
      // here with min = 0. `Math.log(0)` is -Infinity, so an early cut fell back to
      // linear and the pages kept the exact bug this file exists to fix: 86% of the
      // sapphire stock inside the first tenth of the track.
      //
      // So the log DOMAIN starts at a floor while the SCALE still starts at `min`.
      // `opts.floor` names the smallest meaningful unit of the axis; 1 is right for
      // money and is the log1p convention. Position 0 still returns the true `min`,
      // so rule 2 holds and nothing is invented — the first step simply covers
      // min..floor, which on a price axis is a jump of about a dollar.
      var floor = min > 0 ? min : (opts.floor > 0 ? opts.floor : 1);
      var canLog = span > 0 && floor > 0 && max > floor;
      // ⚠️ THE RATIO TEST USES THE FLOOR, NOT `min`. With min = 0, `max / min` is
      // Infinity, which would force log onto every axis that happens to start at
      // zero — including short millimetre ranges that read perfectly well linear.
      var isLog = canLog && (opts.force === 'log' ||
                  (opts.force !== 'linear' && (max / floor) >= this.LOG_RATIO));
      var ratio = isLog ? max / floor : 1;
      var lnRatio = isLog ? Math.log(ratio) : 1;

      return {
        steps: steps,
        isLog: isLog,
        min: min,
        max: max,

        // value -> position. Clamped, so a URL carrying a value outside the pool
        // (stale link, sold-out ceiling) parks the handle at the end instead of
        // off the track.
        toI: function (v) {
          if (span <= 0) return 0;
          v = Math.min(max, Math.max(min, Number(v)));
          // Clamped to the log FLOOR, not to `min` — below the floor there is no
          // logarithm to take, and everything down there maps to the first step.
          var t = isLog ? (Math.log(Math.max(v, floor) / floor) / lnRatio)
                        : ((v - min) / span);
          return Math.round(t * steps);
        },

        // position -> value. RULE 2: the ends are returned, not computed, so they
        // are exact whatever the arithmetic does.
        toV: function (i) {
          if (span <= 0) return min;
          i = Math.min(steps, Math.max(0, Number(i)));
          if (i === 0) return min;
          if (i === steps) return max;
          return isLog ? (floor * Math.pow(ratio, i / steps))
                       : (min + (i / steps) * span);
        }
      };
    }
  };

  window.SRScale = SRScale;
})();
